<script setup>
  import { computed } from 'vue';

  const props = defineProps({
    item: Object,
    depth: Number,
    category: Boolean,
  });

  const component = computed(() => {
    if (props.item.url) {
      return 'VisitorLink';
    } else {
      return 'span';
    }
  });

  const specific = computed(() => {
    if (props.item.url) {
      return {
        href: props.item.url || undefined,
        rel: props.item.rel || undefined,
        target: props.item.target || undefined,
        explicit: props.item.explicit,
      };
    } else {
      return {
        ['data-cc']: props.item.dataCc || undefined,
      };
    }
  });

  const level = computed(() => {
    return `level-${props.depth}`;
  });
</script>
<template>
  <li class="nav-item" :class="level" @click="$emit('close')">
    <component :is="component" v-bind="specific" :class="{ category }" class="nav-item__content">
      <span class="nav-item__unit">
        <span v-if="item.icon" v-html="item.icon" class="nav-item__icon" />
        {{ item.name }}
        <small class="category-available" v-if="depth !== 0 && item.available">
          ({{ item.available }})
        </small>
      </span>
      <IconChevronRight v-if="depth === 0 && item.children.length > 0" class="nav-item-arrow" />
    </component>
    <div class="children-wrapper" v-if="category || item.children.length > 0">
      <ul class="children">
        <HeaderNavItem v-for="item in item.children" :item="item" :depth="depth + 1" @close="$emit('close')" />
      </ul>
    </div>
  </li>
</template>
<style scoped lang="scss">
  .nav-item {

    &__unit {
      display: inline-flex;
      gap: 0.375rem;
    }

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .category-available {
      color: var(--color-dark-gray);
      font-size: 0.75rem;
      font-weight: var(--font-weight-500);
      transition: var(--transition-primary);
    }

    &:hover {
      &.level-0 > .children-wrapper {
        display: block;
        border-bottom: 1px solid var(--color-medium-gray);
        border-right: 1px solid var(--color-medium-gray);
      }
    }

    &.level-2 {
      font-size: 15px;

      .children-wrapper {
        display: none;
      }
    }

    &.level-0 {
      > .children-wrapper {
        position: absolute;
        display: none;
        top: 0;
        left: 299px;
        right: 0;
        height: 100%;
        padding: 1rem 1rem 1rem 3rem;
        background-color: var(--color-primary-white);

        > .children {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          grid-template-rows: auto;
          gap: 2rem 1rem;
          max-height: 100%;
          overflow-y: auto;
          overscroll-behavior: contain;
          list-style: none;
          margin: 0;

          &::-webkit-scrollbar {
            width: 8px;
          }

          &::-webkit-scrollbar-track {
            background-color: var(--color-light-background);
            border-radius: 4px;
          }

          &::-webkit-scrollbar-thumb {
            background-color: var(--primary-color);
            border-radius: 4px;
          }

          @media(max-width: 1650px) {
            grid-template-columns: repeat(3, 1fr);
          }

          @media(max-width: 1200px) {
            grid-template-columns: repeat(2, 1fr);
          }

          & > li > a {
            font-weight: var(--font-weight-700);
            font-size: 15px;
          }
        }
      }

      > .nav-item__content {
        position: relative;

        &.category {
          display: flex;
          justify-content: space-between;
          width: 100%;
          padding: 0.75rem 0.375rem 0.75rem 1.25rem;
          align-items: center;
          font-weight: var(--font-weight-700);
        }

        & svg {
          transform: translateX(-0.375rem);
          font-size: 0.75rem;
          transition-property: color, transform;
          transition-duration: var(--transition-primary-duration);
          transition-timing-function: var(--transition-primary-timing-function);
        }
      }

      &:hover {
        > .nav-item__content.category {
          color: var(--primary-color);

          > svg {
            transform: translateX(0);
          }
        }
      }
    }

    .nav-item__content {
      display: block;
      padding: 0.25rem 0;
      transition-property: color;
      transition-duration: var(--transition-primary-duration);
      transition-timing-function: var(--transition-primary-timing-function);

      &:hover,
      &.active,
      &:hover .category-title .category-available {
        color: var(--primary-color);
      }
    }

    ul {
      list-style: none;
    }
  }
</style>
